<template>
  <div>
    <el-card
      v-if="categoryInfo"
      class="card-container"
      shadow="never"
    >
      <CardHeader
        slot="header"
        :config="categoryInfo"
        :total="total"
        :size="'small'"
        :disabled="fields.length === 0"
        :permission="permission"
        @create="handleCreate"
        @refresh="handleRefresh"
      />
      <div v-if="fields.length > 0">
        <CardFilter
          :category-type="type"
          :categories="categories"
          @search="handleSearch"
        />
        <CardList
          :fields="fields"
          :data="data"
          :loading="loading"
          :permission="permission"
          :category-objects="categoryObjects"
          :object-counts="objectCounts"
          @update="handleUpdate"
          @delete="handleDelete"
        />
        <el-row
          type="flex"
          align="middle"
          justify="end"
          class="mt-l"
        >
          <el-pagination
            background
            :current-page="page"
            :page-sizes="[10, 20, 50, 100, 500, 1000]"
            :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            @size-change="handleChangePageSize"
            @current-change="handleChangePage"
          />
        </el-row>
      </div>
      <div v-else>Bạn chưa cấu hình trường dữ liệu cho danh mục</div>
      <DataFormPopup
        v-if="formVisible"
        :item="selectedItem"
        :visible="formVisible"
        :category="categoryInfo"
        @close="formVisible = false"
        @reload="listData"
      />
    </el-card>
    <div v-else>Bạn chưa cấu hình và Publish các danh mục</div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import CardFilter from "./components/CardFilter.vue";
import CardHeader from "./components/CardHeader.vue";
import CardList from "./components/CardList.vue";
import DataFormPopup from "./popups/DataForm.vue";

import { notification } from "@/utils/common";
import { categoryPer } from "@/utils/permission";
import constants from "@/config/constants";
import commonServices from "@/services/common";
import organizationServices from "@/services/organization";
import documentUtilServices from "@/services/document-util";

export default {
  name: "Categories",
  components: {
    CardHeader,
    CardFilter,
    CardList,
    DataFormPopup,
  },
  data() {
    return {
      loading: false,
      searchText: "",
      page: 1,
      pageSize: constants.PAGE_SIZE,
      total: 0,
      data: [],
      objectCounts: [],
      selectedItem: null,
      formVisible: false,
      type: null,
      permission: categoryPer(),
      fieldType: constants.FIELD_TYPE,
      organizationId: organizationServices.getCurrentOrg()?.id
    };
  },
  computed: {
    ...mapGetters("config", ["allConfigs"]),
    categories() {
      return this.allConfigs.filter((c) => c.type === "categories");
    },
    categoryInfo() {
      return this.categories.find((c) => c.extra.key === this.type);
    },
    fields() {
      return (this.categoryInfo?.extra?.fields || []).sort(
        (a, b) => a.order - b.order
      );
    },
    params() {
      const _params = {
        offset: (this.page - 1) * this.pageSize,
        limit: this.page * this.pageSize,
        locale: "vi",
      };
      _params.filters = {
        type: this.type,
        organization_id: this.organizationId,
        "extra.name": { $regex: this.searchText?.trim() }
      };
      return _params;
    },
    objects() {
      return this.allConfigs.filter((c) => c.type !== "categories");
    },
    categoryObjects() {
      return this.objects.filter((o) => {
        return o.extra.fields.find((f) => f.options?.type === this.type && this.fieldType.SELECT)
      })
    },
    objectCountPayload() {
      return this.categoryObjects.map((o) => {
        const roleAction = constants.OBJECT_TYPES.find((t) => t.value == o.type)?.role_action;
        const categoryField = o.extra.fields.find((f) => f.options?.type === this.type)
        return this.data.map((d) => ({
          document_key: roleAction,
          filters: {
            type: o.extra.key,
            [`extra.${categoryField.key}`]: d.id,
          },
          query_id: d.id
        }))
      }).flat()
    }
  },
  beforeMount() {
    if (this.categories.length > 0) {
      this.type = this.categories[0].extra.key;
      this.listData();
    }
  },
  methods: {
    ...mapActions("category", ["setAllCategories"]),
    listData() {
      this.loading = true;
      commonServices
        .list("categories", this.params)
        .then(async (response) => {
          this.data = response.items.map((item, index) => ({
            ...item,
            stt: index + 1 + this.pageSize * (this.page - 1),
          }));
          this.total = response.meta.total;
          if (this.objectCountPayload.length > 0) {
            await this.getObjectCountData();
          }
          this.loading = false;
        })
        .catch(() => {
          this.data = [];
          this.loading = false;
        });
    },
    async getObjectCountData() {
      documentUtilServices.post_count({
        items: this.objectCountPayload,
      }).then((res) => {
        this.objectCounts = this.objectCountPayload.map((p) => ({
          objectType: p.filters.type,
          categoryId: p.query_id,
          value: res.counts[`${p.document_key}_${p.query_id}`]
        }));
      })
    },
    handleCreate() {
      this.selectedItem = null;
      this.formVisible = true;
    },
    handleUpdate(item) {
      this.selectedItem = item;
      this.formVisible = true;
    },
    handleDelete(item) {
      this.$confirm(`Bạn có chắc chắn muốn xoá không?`, "Xác nhận", {
        confirmButtonText: "Xoá",
        cancelButtonText: "Huỷ",
        type: "warning",
      }).then(() => {
        commonServices
          .remove("categories", item)
          .then(() => {
            if (this.data.length === 1 && this.page > 1) {
              this.page = this.page - 1;
            }
            this.listData();
            this.setAllCategories();
            notification(this, "success", "delete", "danh mục");
          })
          .catch(() => {
            notification(this, "error", "delete", "danh mục");
          });
      });
    },
    handleSearch(payload) {
      this.page = 1;
      this.type = payload.type;
      this.searchText = payload.searchText;
      this.listData();
    },
    handleRefresh() {
      this.selectedItem = null;
      this.page = 1;
      this.searchText = "";
      this.listData();
    },
    handleChangePage(page) {
      this.page = page;
      this.listData();
    },
    handleChangePageSize(size) {
      this.page = 1;
      this.pageSize = size;
      this.listData();
    },
  },
};
</script>

<style lang="scss"></style>
